<script lang="ts" setup>
import PersonaIcon from "@/components/icon/PersonaIcon.vue";
import { useRouter } from "vue-router";
import { ref, watch } from "vue";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { useGalGameHome } from "@/stores/galGameHome";
import { showLoadingToast } from "vant";
import { useDefaultAvatar } from "@/use/common";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
// Props
const props = defineProps<{
  show: boolean;
}>();
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();
// State
const router = useRouter();
const showCreateInfo = ref(true);
const showSelectGame = ref(false);
const store = useGalGameHome();
const selectGameId = ref(0);

const title = ref("");

// Lifecycle
watch(
  () => props.show,
  (value) => {
    if (value) {
      if (store.create[1].length > 0) {
        showCreateInfo.value = true;
      } else {
        showCreateInfo.value = false;
      }
    }
  }
);

const clickSave = async () => {
  emit("update:show", false);
  const item = await store.createGame(title.value);
  useGalGameEditor().resetGame(item);
  router.push({
    name: "galGameEditor",
    params: { uuid: item.uuid },
  });
};

// EventHandler
const onSelect = (id: number) => {
  selectGameId.value = selectGameId.value === id ? 0 : id;
};

const onConfirm = async () => {
  const loadingToast = showLoadingToast({
    message: t("Message.importing"),
  });
  try {
    showSelectGame.value = false;
    emit("update:show", false);
    const item = await store.cloneGame(selectGameId.value);
    useGalGameEditor().resetGame(item);
    router.push({
      name: "galGameEditor",
      params: { uuid: item.uuid },
    });
  } finally {
    loadingToast.close();
  }
};
</script>
<template>
  <VanPopup
    :show="show"
    class="w-[350px] py-10"
    closeable
    position="center"
    round
    @update:show="emit('update:show', $event)"
  >
    <div
      v-if="showCreateInfo"
      class="flex flex-col items-center justify-center w-full h-full gap-10"
    >
      <i class="iconfont icon-script-game text-4xl" />
      <div class="flex flex-col gap-2">
        <button
          class="btn rounded btn-sm btn-primary"
          @click="showCreateInfo = false"
        >
          {{ $t("Buttons.createNewGame") }}
        </button>
        <button class="btn rounded btn-sm" @click="showSelectGame = true">
          {{ $t("Buttons.importFromPublishedGames") }}
        </button>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center w-full h-full gap-5 px-10"
    >
      <div class="flex flex-col items-center justify-center gap-2 w-full">
        <i class="iconfont icon-script-game text-4xl" />
        <div class="text-lg font-bold">
          {{ $t("game.createNewGame") }}
        </div>
      </div>
      <div class="flex flex-col gap-2 w-full">
        <div class="flex flex-col gap-2">
          <span class="font-bold">{{ $t("game.base.name") }}</span>
          <input v-model="title" class="input input-bordered" />
        </div>
      </div>
      <div class="flex flex-col gap-2 justify-between items-center w-full">
        <button
          class="btn rounded btn-primary btn-sm w-full"
          @click="clickSave"
        >
          {{ $t("Buttons.save") }}
        </button>
        <button
          class="btn rounded btn-sm w-full"
          @click="emit('update:show', false)"
        >
          {{ $t("Buttons.cancel") }}
        </button>
      </div>
    </div>
  </VanPopup>
  <VanPopup
    v-model:show="showSelectGame"
    class="h-[80%] overflow-hidden"
    position="bottom"
    round
    teleport="body"
  >
    <div class="h-full w-full overflow-hidden grid grid-rows-[auto_1fr]">
      <div class="navbar h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button
            class="btn btn-ghost btn-sm rounded"
            @click="showSelectGame = false"
          >
            {{ $t("Buttons.cancel") }}
          </button>
        </div>
        <!-- 中 -->
        <div class="navbar-center">
          <div class="font-bold">{{ $t("game.selectGame") }}</div>
        </div>
        <!-- 右 -->
        <div class="navbar-end">
          <button
            :disabled="selectGameId === 0"
            class="btn btn-primary btn-sm rounded"
            @click="onConfirm"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="w-full overflow-y-auto flex flex-col relative">
        <div class="p-2 pt-0 w-full overflow-x-hidden">
          <div
            v-for="item in store.create[1]"
            :key="item"
            :class="[
              selectGameId === item
                ? 'border-primary border rounded'
                : 'border border-transparent ',
            ]"
            class="flex gap-2 items-center p-2 relative"
            @click="onSelect(item)"
          >
            <VanCheckbox
              v-if="selectGameId === item"
              :model-value="item"
              class="shrink-0 pl-2 absolute bottom-2 right-2"
            />
            <div class="w-full flex py-2 h-28 gap-4">
              <div class="h-full aspect-[16/9] relative">
                <VanImage
                  :class="{ 'bg-primary': true }"
                  :src="
                    store.gameMap[item].coverMedia?.url || useDefaultAvatar()
                  "
                  class="h-full aspect-[16/9] object-cover"
                  fit="cover"
                />
              </div>
              <div class="flex-1">
                <h2>{{ store.gameMap[item].name }}</h2>
                <VanTextEllipsis
                  :content="store.gameMap[item].description"
                  class="text-base-content/50 text-sm mt-1"
                  rows="3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VanPopup>
</template>
